import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {merge, of as observableOf, timer} from 'rxjs';
import {catchError, debounce, map, startWith, switchMap} from 'rxjs/operators';
import {MatTable} from '@angular/material/table';
import {FormArray} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router} from '@angular/router';
import {FiltersService, LocationService} from './location.service';
import {Location, SearchFilters} from './location.models';
import {ActivateLocationDialog} from './modals/activate/activate-location-dialog';
import {MatDialog} from '@angular/material/dialog';
import {CreateLocationDialog} from './modals/create/create-location-dialog';
import {DeleteLocationDialog} from './modals/delete/delete-location-dialog';

@Component({
    selector: 'location',
    templateUrl: './location.component.html',
    styleUrls: ['./location.component.css']
})
export class LocationComponent implements AfterViewInit {

    readonly LOCAL_STORAGE_FILTERS_SUFFIX = 'locations-4';
    displayedColumns: string[] = ['vetSuccessLocationName', 'vetSuccessLocationId', 'netSuitePracticeId', 'active', 'status', 'loadDate', 'star'];
    data: Location[] = [];
    isLoadingResults = true;
    resultsLength = 0;
    pageSize = 25;
    controls: FormArray;
    filters: SearchFilters;
    hideInactive = true;
    showFailedOnly = false;

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatTable) table: MatTable<any>;

    constructor(public route: ActivatedRoute,
                public router: Router,
                public dialog: MatDialog,
                public locationService: LocationService,
                private _snackBar: MatSnackBar,
                private filtersService: FiltersService) {
        this.initFilters();
    }

    private initFilters() {
        this.filters = this.filtersService.getFilters(this.LOCAL_STORAGE_FILTERS_SUFFIX);
    }

    ngAfterViewInit() {
        this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

        merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                startWith({}),
                debounce(() => timer(500)),
                switchMap(() => {
                    this.isLoadingResults = true;
                    return this.locationService.find(this.sort.active, this.sort.direction, this.paginator.pageIndex,
                        this.paginator.pageSize, this.filtersService.buildSearchParam(this.filters), this.hideInactive, this.showFailedOnly)
                        .pipe(catchError(() => observableOf(null)));
                }),
                map(result => {
                    this.isLoadingResults = false;
                    // @ts-ignore
                    this.resultsLength = result?.totalElements;
                    const data = result?.content;
                    return data;
                }),
            )
            .subscribe(data => {
                this.data = data;
            });
    }

    updateFilter() {
        this.filtersService.saveFilters(this.filters, this.LOCAL_STORAGE_FILTERS_SUFFIX);
        this.reset();
    }

    reset() {
        this.paginator.pageIndex = 0;
        this.paginator._changePageSize(this.paginator.pageSize);
    }

    resetGrid() {
        this.filtersService.resetFilters(this.LOCAL_STORAGE_FILTERS_SUFFIX);
        this.initFilters();
        this.reset();
    }

    openActivateDialog(location: Location): void {
        const dialogRef = this.dialog.open(ActivateLocationDialog, {
            width: '550px',
            data: {id: location.vetSourceId, name: location.name, active: location.active},
        });

        // tslint:disable-next-line:ter-arrow-parens
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                location.loading = true;
                this.locationService.activate(location.vetSourceId, !location.active)
                    .subscribe((result: Location) => {
                        location.loading = false;
                        location.active = result.active;
                        let activated = 'activated';
                        if (!location.active) {
                            activated = 'deactivated'
                        }
                        this._snackBar.open('Location \'' + location.name + '\' was successfully ' + activated + '!', 'Close', {
                            duration: 5000,
                        });
                    });
            }
        });
    }

    openCreateDialog(location = {} as Location): void {
        const data = Object.assign({}, location);
        const dialogRef = this.dialog.open(CreateLocationDialog, {
            width: '600px',
            data,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                if (location.vetSourceId) {
                    const toUpdate = this.data.filter(item => item.vetSourceId === result.vetSourceId)[0];
                    Object.assign(toUpdate, result);
                } else {
                    // tslint:disable-next-line:no-shadowed-variable
                    this.data.unshift(result);
                    this.resultsLength += 1;
                    this.table.renderRows();
                }
            }
        });
    }

    openDeleteDialog(location: Location): void {
        if (location.active) {
            const dialogRef = this.dialog.open(DeleteLocationDialog, {
                width: '550px',
                data: {id: location.vetSourceId, name: location.name, active: location.active},
            });
        } else {
            const dialogRef = this.dialog.open(DeleteLocationDialog, {
                width: '550px',
                data: {id: location.vetSourceId, name: location.name},
            });

            // tslint:disable-next-line:ter-arrow-parens
            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    location.loading = true;
                    this.locationService.delete(location.vetSourceId)
                        .subscribe((res: { id: any }) => {
                            // tslint:disable-next-line:no-shadowed-variable
                            this.data = this.data.filter(item => item.vetSourceId !== location.vetSourceId);
                            this.resultsLength -= 1;
                            this.table.renderRows()
                            this._snackBar.open('Location \'' + location.name + '\' was successfully deleted!', 'Close', {
                                duration: 5000,
                            });
                        });
                }
            });
        }
    }

    openViewDialog(location: Location): void {
        const data = Object.assign({}, location);
        data.readonly = true;
        const dialogRef = this.dialog.open(CreateLocationDialog, {
            width: '600px',
            data: data,
        });
    }
}
